import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateId } from '@/lib/utils';
import type { Conversation, Message, ChatState } from '@/types';

const createConversation = (title = 'New conversation', id?: string): Conversation => {
  const convId = id ?? generateId();
  const now = new Date().toISOString();
  return {
    id: convId,
    title,
    messages: [] as Message[],
    createdAt: now,
    updatedAt: now,
  };
};

const initialConversation = createConversation();

const initialState: ChatState = {
  session_id: undefined,
  messages: [],
  activeConversationId: initialConversation.id,
  conversations: [initialConversation],
  isLoading: false,
  error: null,
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      const activeId = state.activeConversationId;
      const activeConversation = state.conversations.find((conv) => conv.id === activeId);
      if (!activeConversation) {
        return;
      }

      activeConversation.messages.push(action.payload);
      activeConversation.updatedAt = new Date().toISOString();
      state.messages = activeConversation.messages;
      state.session_id = activeConversation.session_id;
      state.error = null;

      if (
        action.payload.sender === 'user' &&
        (activeConversation.title === 'New conversation' || activeConversation.title.startsWith('Conversation '))
      ) {
        activeConversation.title = action.payload.text.slice(0, 40).trim() || 'New conversation';
      }

      if (action.payload.sender === 'user') {
        state.conversations = state.conversations.filter(
          (conversation) =>
            conversation.id === activeConversation.id || conversation.messages.length > 0,
        );
      }
    },
    updateMessage: (
      state,
      action: PayloadAction<{ id: string; patch: Partial<Message> }>,
    ) => {
      const activeId = state.activeConversationId;
      const activeConversation = state.conversations.find((conv) => conv.id === activeId);
      if (!activeConversation) return;
      const { id, patch } = action.payload;
      const index = activeConversation.messages.findIndex((message) => message.id === id);
      if (index < 0) return;
      activeConversation.messages[index] = {
        ...activeConversation.messages[index],
        ...patch,
      };
      state.messages = activeConversation.messages;
      activeConversation.updatedAt = new Date().toISOString();
    },
    removeMessage: (state, action: PayloadAction<string>) => {
      const activeId = state.activeConversationId;
      const activeConversation = state.conversations.find((conv) => conv.id === activeId);
      if (!activeConversation) return;
      activeConversation.messages = activeConversation.messages.filter((message) => message.id !== action.payload);
      state.messages = activeConversation.messages;
      activeConversation.updatedAt = new Date().toISOString();
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      const activeId = state.activeConversationId;
      const activeConversation = state.conversations.find((conv) => conv.id === activeId);
      if (activeConversation) {
        activeConversation.session_id = action.payload;
      }
      state.session_id = action.payload;
    },
    setActiveConversation: (state, action: PayloadAction<string>) => {
      state.activeConversationId = action.payload;
      const activeConversation = state.conversations.find((conv) => conv.id === action.payload);
      if (activeConversation) {
        state.messages = activeConversation.messages;
        state.session_id = activeConversation.session_id;
      }
      state.error = null;
    },

    hydrateConversationsFromServer: (
      state,
      action: PayloadAction<{ session_id: string; title?: string; updatedAt?: string }[]>,
    ) => {
      const now = new Date().toISOString();
      const existingBySession = new Map(
        state.conversations
          .filter((c) => c.session_id)
          .map((c) => [c.session_id as string, c]),
      );

      const hydrated = action.payload.map((s) => {
        const existing = existingBySession.get(s.session_id);
        if (existing) {
          return {
            ...existing,
            title: s.title ?? existing.title,
            updatedAt: s.updatedAt ?? existing.updatedAt,
          };
        }
        const conv = createConversation(s.title ?? 'Conversation', s.session_id);
        conv.session_id = s.session_id;
        conv.updatedAt = s.updatedAt ?? now;
        return conv;
      });

      const localNonEmpty = state.conversations.filter((c) => !c.session_id && c.messages.length > 0);
      const localEmpty = state.conversations.find((c) => !c.session_id && c.messages.length === 0);
      const empty = localEmpty ?? createConversation();

      state.conversations = [empty, ...localNonEmpty, ...hydrated];
      if (!state.activeConversationId) {
        state.activeConversationId = empty.id;
      }
    },

    hydrateConversationMessages: (
      state,
      action: PayloadAction<{ session_id: string; messages: Message[] }>,
    ) => {
      const { session_id, messages } = action.payload;
      const conv =
        state.conversations.find((c) => c.session_id === session_id) ??
        state.conversations.find((c) => c.id === session_id);
      if (!conv) return;
      conv.messages = messages;
      conv.updatedAt = new Date().toISOString();
      if (state.activeConversationId === conv.id) {
        state.messages = conv.messages;
        state.session_id = conv.session_id;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    resetChat: (state) => {
      // A reset always starts a distinct conversation. Reusing an empty
      // persisted conversation can otherwise carry its prior identity over
      // after a page reload.
      const nextConversation = createConversation();
      state.conversations = [
        nextConversation,
        ...state.conversations.filter(
          (conversation) =>
            conversation.messages.length > 0,
        ),
      ];
      state.activeConversationId = nextConversation.id;
      state.session_id = undefined;
      state.messages = nextConversation.messages;
      state.isLoading = false;
      state.error = null;
    },
    removeConversation: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.conversations = state.conversations.filter((c) => c.id !== id);
      if (state.activeConversationId === id) {
        const next = state.conversations[0] ?? createConversation();
        state.activeConversationId = next.id;
        state.messages = next.messages;
        state.session_id = next.session_id;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  addMessage,
  updateMessage,
  removeMessage,
  setSessionId,
  setActiveConversation,
  hydrateConversationsFromServer,
  hydrateConversationMessages,
  setLoading,
  setError,
  resetChat,
  removeConversation,
  clearError,
} = chatSlice.actions;

export default chatSlice.reducer;
